import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Home,
  Phone,
  CheckCircle2,
  Clock,
  Shield,
  AlertTriangle,
  Send,
  MapPin,
  Star,
  Zap,
  ArrowRight,
  Building2,
  ChevronRight,
  ImageIcon,
  FileText,
  Camera,
  ChevronLeft
} from "lucide-react";
import InfiniteImageCarousel from "@/components/InfiniteImageCarousel";
import useScrollTracking from "@/hooks/useScrollTracking";
import TestimonialsSection from "@/components/TestimonialsSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function StillasPrivatOsloNew() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  
  // Banner state
  const [banner, setBanner] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    email: "",
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Project images state
  const [projectImages, setProjectImages] = useState([]);

  // Fetch active banner and project images on mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await fetch(`${API}/api/banner/active`);
        if (res.ok) {
          const data = await res.json();
          if (data.active && data.banner && data.banner.pages?.includes("privat")) {
            setBanner(data.banner);
          }
        }
      } catch (error) {
        console.debug("Banner fetch failed:", error);
      }
    };
    
    const fetchProjectImages = async () => {
      try {
        // Fetch Privat specific images
        const res = await fetch(`${API}/api/banner/project-images/privat`);
        if (res.ok) {
          const data = await res.json();
          setProjectImages(data.images || []);
        }
      } catch (error) {
        console.debug("Project images fetch failed:", error);
      }
    };
    
    fetchBanner();
    fetchProjectImages();
  }, []);

  // File upload handler - images and PDF
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 20 - images.length;
    if (files.length > remainingSlots) {
      toast.error(lang === "no" 
        ? `Du kan bare legge til ${remainingSlots} fil(er) til (maks 20)` 
        : `You can only add ${remainingSlots} more file(s) (max 20)`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ${lang === "no" ? "er for stor (maks 10MB)" : "is too large (max 10MB)"}`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('folder', 'public_quotes');
        
        const res = await fetch(`${API}/api/projects-v3/public-upload`, {
          method: 'POST',
          body: formDataUpload
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.secure_url);
          const fileType = file.type.includes('pdf') ? 'PDF' : 'Bilde';
          toast.success(lang === "no" ? `${fileType} ${i + 1}/${files.length} lastet opp` : `${fileType} ${i + 1}/${files.length} uploaded`);
        } else {
          const errorData = await res.json().catch(() => ({}));
          toast.error(lang === "no" ? `Kunne ikke laste opp ${file.name}: ${errorData.detail || ''}` : `Could not upload ${file.name}: ${errorData.detail || ''}`);
        }
      } catch (error) {
        toast.error(lang === "no" ? `Feil ved opplasting av ${file.name}` : `Error uploading ${file.name}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setImages(prev => [...prev, ...uploadedUrls]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API}/api/projects-v3/stillastjenester/public-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          company_name: formData.name, // Map name to company_name for API compatibility
          contact_person: formData.name,
          project_address: formData.address,
          source_page: 'private',
          images: images
        })
      });

      if (!res.ok) throw new Error('Failed to submit');
      
      // Google Ads Conversion Tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18035432072/zLNqCNSS8Y0cEIi1-5dD',
          'value': 1.0,
          'currency': 'NOK'
        });
      }
      setSubmitted(true);
      toast.success(lang === "no" ? "Takk! Vi tar kontakt snart." : "Thanks! We'll contact you soon.");
    } catch (err) {
      toast.error(lang === "no" ? "Noe gikk galt. Prøv igjen." : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Translations
  const txt = {
    no: {
      heroTitle: "Trenger du stillas raskt i Oslo?",
      heroSubtitle: "Stillas til hus i Oslo – rask levering og sikker montering",
      heroPoint1: "Gratis befaring",
      heroPoint2: "Rask respons",
      heroPoint3: "Montering innen 24-48 timer",
      ctaQuote: "Få gratis tilbud (rask respons)",
      ctaCall: "Ring nå: 93 92 28 19",
      urgencyBar: "HASTER? Vi har kun få ledige plasser denne uken",
      subtextTitle: "Har du et prosjekt som haster?",
      subtextBody: "Vi leverer og monterer stillas raskt i Oslo og Viken – perfekt for maling, takarbeid og oppussing.",
      subtextUrgent: "Begrenset kapasitet – ta kontakt i dag.",
      whoTitle: "Dette er for deg som:",
      who1: "Trenger stillas raskt",
      who2: "Skal male, rehabilitere eller fikse tak",
      who3: "Ønsker trygg løsning uten stress",
      who4: "Vil ha fast pris og rask respons",
      trustTitle: "Hvorfor velge oss?",
      trust1: "20+ års erfaring med stillas i Oslo og Viken – Doserne AS",
      trust2: "Lokalt firma i Oslo",
      trust3: "Rask oppstart",
      trust4: "Vi bruker HAKI og LAYHER stillas",
      trust5: "Fokus på sikkerhet og kvalitet",
      ctaSectionTitle: "Klar for å starte?",
      ctaSectionBody: "Få et uforpliktende tilbud i dag",
      formTitle: "Få tilbud på stillas",
      formName: "Navn",
      formPhone: "Telefon",
      formAddress: "Adresse",
      formWorkType: "Type arbeid",
      formWorkTypePlaceholder: "F.eks. maling, tak, fasade...",
      formSubmit: "Send forespørsel",
      formSending: "Sender...",
      formNoObligation: "Ingen forpliktelser • Rask respons • Lokalt Oslo-team",
      responseTime: "Vi svarer deg raskt",
      thankYou: "Takk for din henvendelse!",
      thankYouBody: "Vi kontakter deg raskt.",
      sendNew: "Send ny forespørsel",
      forBusiness: "For bedrifter",
      home: "Hjem",
      navTrustBadge: "20+ års erfaring",
      callNowWithNumber: "Ring nå 93 92 28 19"
    },
    en: {
      heroTitle: "Need scaffolding fast in Oslo?",
      heroSubtitle: "Home scaffolding in Oslo – fast delivery and safe assembly",
      heroPoint1: "Free site inspection",
      heroPoint2: "Fast response",
      heroPoint3: "Assembly within 24-48 hours",
      ctaQuote: "Get free quote (fast response)",
      ctaCall: "Call now: 93 92 28 19",
      urgencyBar: "URGENT? We only have a few slots available this week",
      subtextTitle: "Do you have an urgent project?",
      subtextBody: "We deliver and assemble scaffolding quickly in Oslo and Viken – perfect for painting, roofing and renovation.",
      subtextUrgent: "Limited capacity – contact us today.",
      whoTitle: "This is for you who:",
      who1: "Need scaffolding fast",
      who2: "Are painting, renovating or fixing the roof",
      who3: "Want a safe solution without stress",
      who4: "Want fixed pricing and quick response",
      trustTitle: "Why choose us?",
      trust1: "20+ years experience with scaffolding in Oslo and Viken – Doserne AS",
      trust2: "Local company in Oslo",
      trust3: "Quick start",
      trust4: "We use HAKI and LAYHER scaffolding",
      trust5: "Focus on safety and quality",
      ctaSectionTitle: "Ready to start?",
      ctaSectionBody: "Get a non-binding quote today",
      formTitle: "Get scaffolding quote",
      formName: "Name",
      formPhone: "Phone",
      formAddress: "Address",
      formWorkType: "Type of work",
      formWorkTypePlaceholder: "E.g. painting, roof, facade...",
      formSubmit: "Send request",
      formSending: "Sending...",
      formNoObligation: "No obligation • Fast response • Local Oslo team",
      responseTime: "We respond quickly",
      thankYou: "Thank you for your inquiry!",
      thankYouBody: "We'll contact you quickly.",
      sendNew: "Send new request",
      forBusiness: "For businesses",
      home: "Home",
      navTrustBadge: "20+ years experience",
      callNowWithNumber: "Call now 93 92 28 19"
    }
  };
  
  const t2 = txt[lang] || txt.no;
  
  // Scroll tracking for analytics
  useScrollTracking("privat");

  return (
    <>
      <Helmet>
        <title>{lang === "no" ? "Stillas Raskt i Oslo | Rask Levering | AllStillas" : "Fast Scaffolding Oslo | Quick Delivery | AllStillas"}</title>
        <meta name="description" content={t2.heroSubtitle} />
      </Helmet>

      <div className="min-h-screen bg-[#050505] text-white">
        {/* Minimal Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 backdrop-blur-md border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
                <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
              </div>
              <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            </Link>
            
            {/* Trust Badge - Centered */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full">
              <Shield className="w-4 h-4 text-[#F7931A]" />
              <span className="text-[#F7931A] text-xs sm:text-sm font-medium">{t2.navTrustBadge}</span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/" className="hidden sm:block">
                <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                  <Home className="w-4 h-4 mr-1" />
                  {t2.home}
                </Button>
              </Link>
              <LanguageSwitcher variant="full" className="text-white" />
              <a href="tel:+4793922819">
                <Button size="sm" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                  <Phone className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t2.callNowWithNumber}</span>
                </Button>
              </a>
            </div>
          </div>
        </nav>

        {/* PROMO BANNER - Dynamic from Admin */}
        {banner && (
          <div 
            className="fixed top-14 left-0 right-0 z-40 py-2 px-4 shadow-lg"
            style={{ 
              background: `linear-gradient(90deg, ${banner.background_color}, ${banner.background_color}CC, ${banner.background_color})`,
              fontFamily: banner.font_family === 'unbounded' ? "'Unbounded', sans-serif" : 
                         banner.font_family === 'mono' ? "monospace" : "inherit"
            }}
          >
            <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 flex-wrap text-center">
              <span className="text-lg">🎉</span>
              <span 
                className="font-bold"
                style={{ 
                  color: banner.text_color || '#050505',
                  fontSize: banner.title_size || '14px'
                }}
              >
                {lang === "no" ? banner.title_no : banner.title_en}
              </span>
              {banner.discount_text && (
                <span 
                  className="bg-[#050505] font-black px-3 py-0.5 rounded-full"
                  style={{ 
                    color: banner.background_color,
                    fontSize: '14px'
                  }}
                >
                  {banner.discount_text}
                </span>
              )}
              <span 
                style={{ 
                  color: banner.text_color || '#050505',
                  fontSize: banner.desc_size || '12px'
                }}
              >
                {lang === "no" ? banner.description_no : banner.description_en}
              </span>
              {banner.days_remaining > 0 && (
                <span 
                  className="opacity-70"
                  style={{ 
                    color: banner.text_color || '#050505',
                    fontSize: '12px'
                  }}
                >
                  ({banner.days_remaining} {lang === "no" ? "dager igjen" : "days left"})
                </span>
              )}
              <span className="text-lg">🔥</span>
            </div>
          </div>
        )}

        {/* HERO SECTION - adjust padding if banner is active */}
        <section className={`${banner ? 'pt-36' : 'pt-20'} pb-16 px-4`}>
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Cross-link badges */}
            <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
              {/* Current Page Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full">
                <Home className="w-4 h-4 text-[#F7931A]" />
                <span className="text-sm text-[#F7931A] font-medium">
                  {lang === "no" ? "For private" : "For homeowners"}
                </span>
              </div>
              
              {/* Cross-link to B2B Page */}
              <Link to="/stillasfirma-oslo">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5]/10 border border-[#4F46E5]/30 rounded-full hover:bg-[#4F46E5]/20 transition-colors cursor-pointer">
                  <Building2 className="w-4 h-4 text-[#4F46E5]" />
                  <span className="text-sm text-[#4F46E5] font-medium">
                    {lang === "no" ? "For bedrifter" : "For businesses"}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#4F46E5]" />
                </div>
              </Link>
            </div>
            
            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Unbounded'] font-bold text-white mb-4 leading-tight">
              🏗️ {t2.heroTitle}
            </h1>
            
            <p className="text-lg sm:text-xl text-[#94A3B8] mb-4">
              {t2.heroSubtitle}
            </p>
            
            {/* Doserne AS Trust Line */}
            <p className="text-sm text-[#F7931A] mb-8 font-medium">
              {lang === "no" 
                ? "Tjenesten leveres av Doserne AS – et lokalt stillasfirma i Oslo" 
                : "Service provided by Doserne AS – a local scaffolding company in Oslo"}
            </p>

            {/* Key Points */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-8">
              {[
                { icon: <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />, text: t2.heroPoint1 },
                { icon: <Clock className="w-5 h-5 text-[#23F7DD]" />, text: t2.heroPoint2 },
                { icon: <Zap className="w-5 h-5 text-[#23F7DD]" />, text: t2.heroPoint3 }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-white">
                  {item.icon}
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#form">
                <Button size="lg" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg px-8 py-6 rounded-xl" data-testid="hero-cta-quote">
                  {t2.ctaQuote}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="tel:+4793922819">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg px-8 py-6 rounded-xl">
                  <Phone className="w-5 h-5 mr-2" />
                  {t2.ctaCall}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* SUBTEXT SECTION */}
        <section className="py-12 px-4 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-4">
              ⏰ {t2.subtextTitle}
            </h2>
            <p className="text-[#94A3B8] text-lg">
              {t2.subtextBody}
            </p>
          </div>
        </section>

        {/* WHO IS THIS FOR + TRUST - Side by Side */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {/* WHO IS THIS FOR */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <h3 className="text-xl font-['Unbounded'] font-bold text-white mb-6 flex items-center gap-2">
                  🎯 {t2.whoTitle}
                </h3>
                <ul className="space-y-3">
                  {[t2.who1, t2.who2, t2.who3, t2.who4].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#94A3B8]">
                      <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* TRUST SECTION */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <h3 className="text-xl font-['Unbounded'] font-bold text-white mb-6 flex items-center gap-2">
                  🔒 {t2.trustTitle}
                </h3>
                <ul className="space-y-3">
                  {/* FIRST: Key credibility bullet - 20+ years experience */}
                  <li className="flex items-center gap-3 text-white font-medium">
                    <Star className="w-5 h-5 text-[#F7931A] flex-shrink-0" />
                    <span>{t2.trust1}</span>
                  </li>
                  {/* Rest of trust points */}
                  {[t2.trust2, t2.trust3, t2.trust4, t2.trust5].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#94A3B8]">
                      <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-12 px-4 bg-gradient-to-r from-[#23F7DD]/10 via-[#23F7DD]/5 to-[#23F7DD]/10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-3">
              🚀 {t2.ctaSectionTitle}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-6">
              {t2.ctaSectionBody}
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <span className="flex items-center gap-2 text-[#23F7DD]">
                <Clock className="w-4 h-4" />
                {t2.heroPoint2}
              </span>
              <span className="flex items-center gap-2 text-[#23F7DD]">
                <CheckCircle2 className="w-4 h-4" />
                {t2.heroPoint1}
              </span>
            </div>
          </div>
        </section>

        {/* PROJECT GALLERY - Moved up for better engagement */}
        {projectImages.length > 0 && (
          <section className="py-12 px-4 bg-[#0A0A0A] overflow-hidden">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white mb-3 flex items-center justify-center gap-2">
                  <Camera className="w-6 h-6 text-[#F7931A]" />
                  {lang === "no" ? "Tidligere prosjekter" : "Previous Projects"}
                </h2>
                <p className="text-[#94A3B8] text-sm">
                  {lang === "no" 
                    ? "Vi har levert stillas til en rekke prosjekter i Oslo."
                    : "We have delivered scaffolding to numerous projects in Oslo."}
                </p>
              </div>
              
              {/* Infinite Scrolling Carousel */}
              <InfiniteImageCarousel 
                images={projectImages}
                speed={35}
                pauseOnHover={true}
                className="py-2"
              />
              
              {/* Scroll hint */}
              <p className="text-center text-[#64748B] text-xs mt-3 flex items-center justify-center gap-2">
                <ChevronLeft className="w-3 h-3" />
                {lang === "no" ? "Scroll eller dra for å se flere" : "Scroll or drag to see more"}
                <ChevronRight className="w-3 h-3" />
              </p>
            </div>
          </section>
        )}

        {/* ABOUT / TRUST SECTION - Hvem står bak AllStillas (SHORT VERSION) */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-white/5 to-white/10 border-white/10">
              <CardContent className="p-6">
                <h3 className="text-xl font-['Unbounded'] font-bold text-white mb-4 flex items-center gap-2">
                  👷 {lang === "no" ? "Hvem står bak AllStillas?" : "Who is behind AllStillas?"}
                </h3>
                
                <ul className="space-y-2">
                  <li className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                    <span>{lang === "no" ? "Doserne AS – lokalt stillasfirma i Oslo" : "Doserne AS – local scaffolding company in Oslo"}</span>
                  </li>
                  <li className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                    <span>{lang === "no" ? "Profesjonelle montører" : "Professional assemblers"}</span>
                  </li>
                  <li className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                    <span>{lang === "no" ? "HAKI og LAYHER stillassystemer" : "HAKI and LAYHER scaffolding systems"}</span>
                  </li>
                  <li className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                    <span>{lang === "no" ? "Fokus på sikkerhet og rask respons" : "Focus on safety and fast response"}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FORM SECTION */}
        <section id="form" className="py-16 px-4 bg-[#0A0A0A]">
          <div className="max-w-xl mx-auto">
            {submitted ? (
              <Card className="bg-[#23F7DD]/10 border-[#23F7DD]/30">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#23F7DD] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">{t2.thankYou}</h3>
                  <p className="text-[#94A3B8] mb-6">{t2.thankYouBody}</p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", phone: "", address: "", email: "", description: "" });
                      setImages([]);
                    }}
                    variant="outline"
                    className="border-[#23F7DD] text-[#23F7DD] hover:bg-[#23F7DD]/10"
                  >
                    {t2.sendNew}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-[#F7931A]/10 to-[#23F7DD]/5 border-[#F7931A]/30">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white mb-2 text-center">
                    📋 {t2.formTitle}
                  </h3>
                  
                  {/* No Obligation Badge */}
                  <p className="text-center text-[#23F7DD] text-sm mb-6 font-medium">
                    {t2.formNoObligation}
                  </p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-white">{t2.formName} *</Label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        placeholder="Ola Nordmann"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white">{t2.formAddress} *</Label>
                      <Input
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        placeholder="Gateadresse, Oslo"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white">{t2.formWorkType}</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="bg-white/10 border-white/20 text-white mt-1 min-h-[80px]"
                        placeholder={t2.formWorkTypePlaceholder}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">{t2.formPhone} *</Label>
                        <Input
                          required
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="bg-white/10 border-white/20 text-white mt-1"
                          placeholder="+47 xxx xx xxx"
                        />
                      </div>
                      <div>
                        <Label className="text-white">E-post</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="bg-white/10 border-white/20 text-white mt-1"
                          placeholder="din@epost.no"
                        />
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    <div>
                      <Label className="text-white mb-2 block">
                        {lang === "no" ? "Legg ved filer (valgfritt)" : "Add files (optional)"}
                      </Label>
                      <div className="space-y-3">
                        <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                          <ImageIcon className="w-5 h-5 text-[#F7931A]" />
                          <span className="text-white/70">
                            {uploading 
                              ? (lang === "no" ? "Laster opp..." : "Uploading...") 
                              : (lang === "no" ? `Velg bilder/PDF (maks 20, ${20 - images.length} igjen)` : `Select images/PDF (max 20, ${20 - images.length} left)`)}
                          </span>
                          <input
                            type="file"
                            accept="image/*,.pdf,application/pdf"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploading || images.length >= 20}
                          />
                        </label>
                        
                        {images.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {images.map((url, index) => (
                              <div key={index} className="relative group">
                                {url.includes('/raw/upload/') || url.toLowerCase().endsWith('.pdf') ? (
                                  <div className="w-20 h-20 bg-white/10 rounded-lg border border-white/20 flex flex-col items-center justify-center">
                                    <FileText className="w-6 h-6 text-red-400" />
                                    <span className="text-[10px] text-[#94A3B8]">PDF</span>
                                  </div>
                                ) : (
                                  <img 
                                    src={url} 
                                    alt={`Upload ${index + 1}`} 
                                    className="w-20 h-20 object-cover rounded-lg border border-white/20"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeImage(index)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#F7931A] text-white hover:bg-[#FFB347] font-bold text-lg py-6 mt-2"
                      data-testid="form-submit"
                    >
                      {submitting ? t2.formSending : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t2.formSubmit}
                        </>
                      )}
                    </Button>
                  </form>
                  
                  {/* Response Time Badge */}
                  <div className="mt-4 text-center">
                    <Badge className="bg-white/10 text-[#94A3B8] border-white/20">
                      <Clock className="w-3 h-3 mr-1" />
                      {t2.responseTime}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16 px-4 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-8 text-center">
              ❓ {lang === "no" ? "Ofte stilte spørsmål" : "Frequently Asked Questions"}
            </h2>
            
            <div className="space-y-4">
              {/* FAQ 0 - PRICE (eliminates biggest barrier!) */}
              <Card className="bg-gradient-to-r from-[#F7931A]/10 to-transparent border-[#F7931A]/30">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                    <span className="text-[#F7931A]">?</span>
                    {lang === "no" ? "Hva koster stillas?" : "How much does scaffolding cost?"}
                  </h3>
                  <p className="text-[#94A3B8] mb-2">
                    {lang === "no" 
                      ? "Pris avhenger av prosjektet – få et gratis tilbud på under 1 time."
                      : "Price depends on the project – get a free quote in under 1 hour."}
                  </p>
                  <p className="text-[#23F7DD] text-sm font-medium">
                    👉 {lang === "no" ? "Send forespørsel nå – ingen forpliktelser." : "Send request now – no obligations."}
                  </p>
                </CardContent>
              </Card>

              {/* FAQ 1 */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                    <span className="text-[#F7931A]">?</span>
                    {lang === "no" ? "Trenger jeg å gjøre noe før montering?" : "Do I need to do anything before assembly?"}
                  </h3>
                  <p className="text-[#94A3B8] mb-2">
                    {lang === "no" 
                      ? "Nei – vi tar oss av alt. Vi planlegger, leverer og monterer stillaset for deg. Du trenger ikke å gjøre noe."
                      : "No – we take care of everything. We plan, deliver and assemble the scaffolding for you. You don't need to do anything."}
                  </p>
                  <p className="text-[#23F7DD] text-sm font-medium">
                    👉 {lang === "no" ? "Klar til å starte? Få tilbud nå." : "Ready to start? Get a quote now."}
                  </p>
                </CardContent>
              </Card>

              {/* FAQ 2 */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                    <span className="text-[#F7931A]">?</span>
                    {lang === "no" ? "Hvor lenge kan stillaset stå?" : "How long can the scaffolding stay?"}
                  </h3>
                  <p className="text-[#94A3B8] mb-2">
                    {lang === "no" 
                      ? "Så lenge prosjektet ditt krever det. Vi tilpasser varigheten etter dine behov – helt fleksibelt."
                      : "As long as your project requires. We adjust the duration to your needs – completely flexible."}
                  </p>
                  <p className="text-[#23F7DD] text-sm font-medium">
                    👉 {lang === "no" ? "Kontakt oss i dag for en løsning som passer deg." : "Contact us today for a solution that suits you."}
                  </p>
                </CardContent>
              </Card>

              {/* FAQ 3 */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                    <span className="text-[#F7931A]">?</span>
                    {lang === "no" ? "Er stillas trygt for privat bruk?" : "Is scaffolding safe for private use?"}
                  </h3>
                  <p className="text-[#94A3B8] mb-2">
                    {lang === "no" 
                      ? "Ja – 100% trygt. Vi bruker profesjonelle systemer (HAKI / LAYHER) og monterer etter alle sikkerhetskrav."
                      : "Yes – 100% safe. We use professional systems (HAKI / LAYHER) and assemble according to all safety requirements."}
                  </p>
                  <p className="text-[#23F7DD] text-sm font-medium">
                    👉 {lang === "no" ? "Trygg løsning uten stress – få tilbud nå." : "Safe solution without stress – get a quote now."}
                  </p>
                </CardContent>
              </Card>

              {/* FAQ 4 */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                    <span className="text-[#F7931A]">?</span>
                    {lang === "no" ? "Hvor raskt kan dere levere?" : "How fast can you deliver?"}
                  </h3>
                  <p className="text-[#94A3B8] mb-2">
                    {lang === "no" 
                      ? "Ofte innen 24-48 timer. Vi har kapasitet for raske leveranser i Oslo og Viken."
                      : "Often within 24-48 hours. We have capacity for fast deliveries in Oslo and Viken."}
                  </p>
                  <p className="text-[#F7931A] text-sm font-medium">
                    ⚡ {lang === "no" ? "Ta kontakt i dag!" : "Contact us today!"}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto text-center">
            {/* Extra Boost Text */}
            <p className="text-[#94A3B8] text-sm mb-4">
              {lang === "no" 
                ? "Doserne AS har levert stillasprosjekter i Oslo i flere år" 
                : "Doserne AS has delivered scaffolding projects in Oslo for several years"}
            </p>
            <a href="tel:+4793922819">
              <Button size="lg" className="bg-[#F7931A] text-white hover:bg-[#FFB347] font-bold text-lg px-10 py-6 rounded-xl">
                <Phone className="w-5 h-5 mr-2" />
                {t2.ctaCall}
              </Button>
            </a>
          </div>
        </section>

        {/* TESTIMONIALS SECTION */}
        <TestimonialsSection
          title={lang === "no" ? "Hva kundene våre sier" : "What our customers say"}
          subtitle={lang === "no" ? "Ekte tilbakemeldinger fra fornøyde kunder" : "Real feedback from happy customers"}
          variant="dark"
        />

        {/* Simple Footer */}
        <footer className="py-6 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <Link to="/" className="text-[#94A3B8] hover:text-white text-sm">{t2.home}</Link>
                <Link to="/stillasfirma-oslo" className="text-[#94A3B8] hover:text-white text-sm">{t2.forBusiness}</Link>
              </div>
              <p className="text-sm text-[#64748B]">© 2026 Doserne AS</p>
            </div>
            {/* Obligatory Legal Line */}
            <p className="text-xs text-[#475569] text-center">
              {lang === "no" 
                ? "AllStillas er en tjeneste levert av Doserne AS | Org.nr: 916 925 953" 
                : "AllStillas is a service provided by Doserne AS | Org.nr: 916 925 953"}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
